import React, { useState, useRef, useEffect } from 'react';
export default function Marquee({
  children,
  speed = 60,
  direction = 'left',
  pauseOnHover = true,
  className = '',
  gradient = true,
  onClick = undefined,
  style,
  alwaysScroll = false,
}) {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [duration, setDuration] = useState(0); // seconds

  // measure overflow and compute animation duration
  useEffect(() => {
    const container = containerRef.current;
    const content = contentRef.current;
    if (!container || !content) return;

    const compute = () => {
      const containerWidth = Math.ceil(container.clientWidth);
      const contentWidth = Math.ceil(content.scrollWidth);

      if (contentWidth > containerWidth + 1) {
        setShouldScroll(true);
        // distance to scroll = contentWidth + containerWidth (so it fully leaves)
        const distance = contentWidth + containerWidth;
        setDuration(distance / Math.max(1, speed)); // seconds
      } else {
        setShouldScroll(false);
        setDuration(0);
      }
    };

    compute();
    // recompute on resize
    const ro = new ResizeObserver(compute);
    ro.observe(container);
    ro.observe(content);
    return () => ro.disconnect();
  }, [children, speed]);

  // Respect user's reduced motion preference
  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const animationStyle =
    (alwaysScroll || shouldScroll) && !prefersReducedMotion
      ? {
          // we'll animate transform from 0 to -translateX (using CSS keyframes below)
          animation: `${alwaysScroll ? 20 : duration}s linear 0s infinite marquee-${direction}`,
        }
      : undefined;

  return (
    <div
      ref={containerRef}
      className={`marquee-root ${className}`}
      style={{
        ...style,
        position: 'relative',
        overflow: 'hidden',
        display: 'flex', // center content vertically
        alignItems: 'center', // vertical centering
        width: '100%',
      }}
      // allow pausing animation on hover/focus if requested
      onMouseEnter={
        pauseOnHover
          ? (e) => {
              const el = contentRef.current;
              if (el) el.style.animationPlayState = 'paused';
            }
          : undefined
      }
      onMouseLeave={
        pauseOnHover
          ? (e) => {
              const el = contentRef.current;
              if (el) el.style.animationPlayState = 'running';
            }
          : undefined
      }
      onClick={onClick}
    >
      {/* content wrapper (we duplicate the content when scrolling so it appears continuous) */}
      <div
        ref={contentRef}
        className="marquee-content"
        style={{
          display: 'inline-block',
          whiteSpace: 'nowrap',
          willChange: 'transform',
          ...animationStyle,
        }}
        tabIndex={0} // make focusable so keyboard users can pause via focus
        onFocus={
          pauseOnHover
            ? (e) => {
                e.currentTarget.style.animationPlayState = 'paused';
              }
            : undefined
        }
        onBlur={
          pauseOnHover
            ? (e) => {
                e.currentTarget.style.animationPlayState = 'running';
              }
            : undefined
        }
      >
        <span
          className="marquee-inner"
          style={{ paddingRight: 40, display: 'inline-flex', alignItems: 'center', height: '100%' }}
        >
          {children}
        </span>

        {/* duplicate for smooth continuous scroll only when scrolling */}
        {(shouldScroll || alwaysScroll) && !prefersReducedMotion && (
          <span
            className="marquee-inner"
            style={{
              paddingRight: 40,
              display: 'inline-flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {children}
          </span>
        )}
      </div>

      {/* optional gradient overlay */}
      {gradient && (
        <>
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: 28,
              pointerEvents: 'none',
              // background: "linear-gradient(90deg, rgba(255,255,255,1), rgba(255,255,255,0))",
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: 28,
              pointerEvents: 'none',
              // background: "linear-gradient(-90deg, rgba(255,255,255,1), rgba(255,255,255,0))",
            }}
          />
        </>
      )}

      {/* Inject keyframes styles for left/right */}
      <style jsx="true">{`
        @keyframes marquee-left {
          0% {
            transform: translateX(0);
          }
          100% {
            transform: translateX(-50%);
          } /* because we duplicated content, -50% moves one full content width */
        }
        @keyframes marquee-right {
          0% {
            transform: translateX(-50%);
          }
          100% {
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}
